import type {
  AndroidDetailsDataPaths,
  AndroidDeviceDetail,
  AndroidDevicePosture,
  AndroidEncryptionStatus,
  AndroidMemoryEvent,
  AndroidPasswordRequirements,
  AndroidPowerManagementEvent,
  AndroidTelephonyInfo,
} from 'src/schemas/device/android-device-detail.schema';
import { formatDiskSize } from 'src/utils/SizeConversionUtils';
import type { IDetailCategory } from './common';

export const sortByCreateTime = (a, b) => {
  return new Date(b.createTime).getTime() - new Date(a.createTime).getTime();
};
export const getLatestEventOfType = (
  eventType: string,
  events: Array<AndroidMemoryEvent | AndroidPowerManagementEvent>,
) => {
  if (!Array.isArray(events) || typeof eventType !== 'string') return '';
  const sortedEvents = events
    .filter((event) => event.eventType === eventType)
    .sort(sortByCreateTime);
  return sortedEvents[0];
};
export const macroCaseToTitleCase = (str: string) => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const general: IDetailCategory<AndroidDetailsDataPaths> = {
  osVersion: {
    dataKey: 'details.android_version',
    title: 'OS Version',
  },
  osBuild: {
    dataKey: 'details.details.softwareInfo.androidBuildNumber',
    title: 'OS Build',
  },
  apiLevel: {
    dataKey: 'details.details.apiLevel',
    title: 'API Level',
  },
  manufacturer: {
    dataKey: 'details.manufacturer',
    title: 'Manufacturer',
  },
  modelName: {
    dataKey: 'details.model',
    title: 'Model Name',
  },
  serialNumber: {
    dataKey: 'details.serial_number',
    title: 'Serial Number',
  },
  androidEnterpriseId: {
    dataKey: 'details.details.name',
    title: 'Enterprise ID',
    transform: (value: string) => {
      const match = value.match(/^enterprises\/([^/]+)\/devices\/[^/]+$/);
      return match ? match[1] : undefined;
    },
  },
  androidEnterpriseDeviceId: {
    dataKey: 'details.details.name',
    title: 'Enterprise Device ID',
    transform: (value: string) => {
      const match = value.match(/^enterprises\/[^/]+\/devices\/([^/]+)$/);
      return match ? match[1] : undefined;
    },
  },
};

export const hardwareOverview: IDetailCategory<AndroidDetailsDataPaths> = {
  batteryLevel: {
    dataKey: 'details.details.powerManagementEvents',
    title: 'Battery Level',
    transform: (value: AndroidPowerManagementEvent[]) => {
      const batteryLevelEvent = getLatestEventOfType(
        'BATTERY_LEVEL_COLLECTED',
        value,
      );
      const batteryLevel = (batteryLevelEvent as AndroidPowerManagementEvent)
        ?.batteryLevel;
      return batteryLevel ? `${batteryLevel}%` : undefined;
    },
  },
  availbleDeviceCapacity: {
    dataKey: 'details.details.memoryEvents',
    title: 'Available Device Capacity',
    transform: (value: AndroidMemoryEvent[]) => {
      const internalStorageEvent = getLatestEventOfType(
        'INTERNAL_STORAGE_MEASURED',
        value,
      );
      const byteCount = (internalStorageEvent as AndroidMemoryEvent)?.byteCount;
      return byteCount ? formatDiskSize(parseInt(byteCount)) : undefined;
    },
  },
  deviceCapacity: {
    dataKey: 'details.details.memoryInfo.totalInternalStorage',
    title: 'Device Capacity',
    transform: (value: number) => formatDiskSize(value),
  },
  totalRam: {
    dataKey: 'details.details.memoryInfo.totalRam',
    title: 'Total RAM',
    transform: (value: number) => formatDiskSize(value),
  },
  wifiAdress: {
    dataKey: 'details.details.networkInfo.wifiMacAddress',
    title: 'Wi-Fi Address',
  },
  cellularTechnology: {
    dataKey: 'details.details.networkInfo.imei',
    title: 'Cellular Technology',
    transform: (
      _value: string,
      _dataKey: string,
      { details }: AndroidDeviceDetail = {},
    ) => {
      const imei = details.details.networkInfo.imei;
      const meid = details.details.networkInfo.meid;
      if (imei && meid) return `IMEI and MEID`;
      return imei ? 'IMEI' : meid ? 'MEID' : undefined;
    },
  },
};

export const mdm: IDetailCategory<AndroidDetailsDataPaths> = {
  managementMode: {
    dataKey: 'details.details.managementMode',
    title: 'Management Mode',
    transform: (value: string) => macroCaseToTitleCase(value),
  },
  ownership: {
    dataKey: 'details.details.ownership',
    title: 'Ownership',
    transform: (value: string) => macroCaseToTitleCase(value),
  },
};

export const cellular: IDetailCategory<AndroidDetailsDataPaths> = {
  phoneNumber: {
    dataKey: 'details.details.networkInfo.telephonyInfos',
    title: 'Phone Number',
    transform: (value: AndroidTelephonyInfo[]) => value?.[0]?.phoneNumber,
  },
  carrierName: {
    dataKey: 'details.details.networkInfo.telephonyInfos',
    title: 'Carrier Name',
    transform: (value: AndroidTelephonyInfo) => value?.[0]?.carrierName,
  },
  imei: {
    dataKey: 'details.details.networkInfo.imei',
    title: 'IMEI',
  },
  iccid: {
    dataKey: 'details.details.networkInfo.telephonyInfos',
    title: 'ICCID',
    transform: (value: AndroidTelephonyInfo) => value?.[0]?.iccId,
  },
  meid: {
    dataKey: 'details.details.networkInfo.meid',
    title: 'MEID',
  },
};

export const security: IDetailCategory<AndroidDetailsDataPaths> = {
  isEncrypted: {
    dataKey: 'details.details.deviceSettings.isEncrypted',
    title: 'Storage Encryption',
    transform: (value: boolean) => (value ? 'Enabled' : 'Disabled'),
  },
  encryptionStatus: {
    dataKey: 'details.details.deviceSettings.encryptionStatus',
    title: 'Encryption Status',
    transform: (value: AndroidEncryptionStatus) => {
      if (value === 'ENCRYPTION_STATUS_UNSPECIFIED') return 'Unspecified';
      if (value === 'ACTIVE_DEFAULT_KEY') return 'Active with default key';
      if (value === 'ACTIVE_PER_USER') return 'Active with user key';
      return macroCaseToTitleCase(value);
    },
  },
  devicePosture: {
    dataKey: 'details.details.securityPosture.devicePosture',
    title: 'Device Posture',
    transform: (value: AndroidDevicePosture) => {
      if (value === 'POSTURE_UNSPECIFIED') return 'Unspecified';
      return macroCaseToTitleCase(value);
    },
  },
  isDeviceSecure: {
    dataKey: 'details.details.deviceSettings.isDeviceSecure',
    title: 'Passcode',
    transform: (value: boolean) => (value ? 'Enabled' : 'Disabled'),
  },
  verifyAppsEnabled: {
    dataKey: 'details.details.deviceSettings.verifyAppsEnabled',
    title: 'Google Play Protect',
    transform: (value: boolean) => (value ? 'Enabled' : 'Disabled'),
  },
  passcodePresent: {
    dataKey: 'details.details.appliedPasswordPolicies',
    title: 'Passcode Present',
    transform: (value: AndroidPasswordRequirements[]) =>
      value?.length > 0 ? 'Yes' : 'No',
  },
  passcodeCompliant: {
    dataKey: 'details.details.policyCompliant',
    title: 'Passcode Compliant',
    transform: (
      value: boolean,
      _dataKey: string,
      details: AndroidDeviceDetail,
    ) =>
      value && details?.details?.details?.appliedPasswordPolicies?.length > 0
        ? 'Yes'
        : 'No',
  },
};

export const AndroidCategory = {
  General: general,
  Hardware: hardwareOverview,
  Management: mdm,
  Cellular: cellular,
  Security: security,
};
